import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  availableDuctedFuels,
  boundedCodesFor,
  ductedUniverseCodes
} from 'components/equipment-page/ducted-facet-merge';
import {
  clearDuctedChassisFilter,
  resetDuctedSystemTypeCodes
} from 'features/ui';
import useIsCompatibilityPathway from 'hooks/use-is-compatibility-pathway';
import { useEffect, useMemo } from 'react';
import {
  ductedPropaneSeedDescription,
  ductedSeedChassisSizes,
  ductedSeedFuels,
  ductedSeedOrientations,
  everyDuctedSystemWantsPackagedUnit,
  getChooseSystemsEligibleDuctedSystems
} from 'utils/existing-equipment';

// The once-per-results-context ductedFilters reset (AHA-6681 CS-1, seeds
// widened in AHA-6796), extracted from useDuctedCompatibilityFetch so BOTH
// surfaces sharing the filter state apply it: Choose Systems (via the fetch
// hook) and the Existing Equipment page (which now renders the fuel/chassis
// rows — the seed must land while the user is still there, or their first
// CS visit would clobber an EE-made selection with a late seed).
// resultsKey identifies the LES-4 results context (per-system summary code
// lists); the reducer no-ops on a repeated key, so mounting this hook from
// several components at once is free — exactly one reset applies per
// context, and user edits stick within it. Manual-sourced fuel/chassis
// values survive even a NEW context (the reducer's resolveContext*
// composition); universeFuels rides the payload so the manual fuel prune
// keys on the same full-universe union as the seed guard.
//
// Returns the derivations the Existing Equipment host rows need, exposed
// from here so the rows and the reset can never diverge on them:
// - resetApplied: this context's reset has landed (the fetch hook gates on
//   it);
// - boundUnionCodes: the union of the eligible systems' recommended bounds
//   (still the Chassis row's render gate);
// - universeCodes: the FULL eligible universe (AHA-6796 fuel chips = full
//   universe — ductedUniverseCodes over the per-system catalogs, bound
//   fallback) — the fuel row's no-invisible-filter availability universe;
// - seedChassisSizes / seedOrientations / propaneSeedDescription: the
//   equipment-hint inputs.
export default function useDuctedFilterContextReset() {
  const dispatch = useAppDispatch();
  const isDucted = useIsCompatibilityPathway();
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const compatibleResultsBySystem = useAppSelector(
    (state) => state.structure.compatibleResultsBySystem
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const wantCompositionPicksBySystem = useAppSelector(
    (state) => state.structure.wantCompositionPicksBySystem
  );
  const recommendationCatalogCodesBySystem = useAppSelector(
    (state) => state.structure.recommendationCatalogCodesBySystem
  );
  const recommendationSelectionsLoaded = useAppSelector(
    (state) => state.structure.recommendationSelectionsLoaded
  );
  const resetResultsKey = useAppSelector(
    (state) => state.ui.ductedFilters.resetResultsKey
  );

  // Same eligibility as every other Choose Systems consumer (virtual-tile
  // haves included).
  const eligibleSystems = useMemo(
    () =>
      isDucted
        ? getChooseSystemsEligibleDuctedSystems(
            existingSystems,
            recommendationSelectionsBySystem,
            wantCompositionPicksBySystem
          )
        : [],
    [
      isDucted,
      existingSystems,
      recommendationSelectionsBySystem,
      wantCompositionPicksBySystem
    ]
  );

  // The results context identity: per-system summary code lists — a
  // context change discards any stale seed-sourced selection instead of
  // carrying it into results it may not apply to.
  const resultsKey = useMemo(
    () =>
      eligibleSystems
        .map((system) => {
          const summaryCodes =
            boundedCodesFor(compatibleResultsBySystem[system.uniqueId]) ?? [];
          return `${system.uniqueId}:${summaryCodes.join(',')}`;
        })
        .join('|'),
    [eligibleSystems, compatibleResultsBySystem]
  );

  // First-appearance-ordered union of the per-system recommended bounds —
  // the seed guard's input AND the EE fuel row's availability universe.
  // Content-keyed on resultsKey (the summaries churn references on
  // unrelated store writes).
  const boundUnionCodes = useMemo(
    () => {
      const codes: string[] = [];
      const seen = new Set<string>();
      eligibleSystems.forEach((system) => {
        (
          boundedCodesFor(compatibleResultsBySystem[system.uniqueId]) ?? []
        ).forEach((code) => {
          if (!seen.has(code)) {
            seen.add(code);
            codes.push(code);
          }
        });
      });
      return codes;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resultsKey]
  );

  // The FULL eligible universe (AHA-6796 fuel chips = full universe): the
  // shared ductedUniverseCodes derivation over the per-system catalogs with
  // per-system bound fallback. Keyed on resultsKey (bound content) + the
  // catalog map identity — the setSystemRecommendationCatalogCodes reducer
  // content-compares before writing, so the map reference only churns when
  // a catalog actually changes.
  const universeCodes = useMemo(
    () =>
      ductedUniverseCodes(
        eligibleSystems,
        recommendationCatalogCodesBySystem,
        compatibleResultsBySystem
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resultsKey, recommendationCatalogCodesBySystem]
  );

  // Fuel seed for the once-per-context reset (AHA-6796 Fuel chips): ['lp']
  // when a propane furnace (or a saved virtualFuel 'LP') rides any eligible
  // system AND the FULL universe offers an lp code (renderability — the
  // chip row now renders from the universe, so the seed guard keys on the
  // same set). Derived per dispatch — the reducer's repeated-key no-op is
  // what makes it seed-once.
  const seedFuels = useMemo(
    () =>
      ductedSeedFuels(
        eligibleSystems,
        recommendationSelectionsBySystem,
        universeCodes
      ),
    [eligibleSystems, recommendationSelectionsBySystem, universeCodes]
  );

  // The new context's fuel vocabulary — what a preserved MANUAL fuel
  // selection prunes to in the reducer (same derivation as the rows'
  // availability, so a kept selection is always renderable/clearable).
  // The FULL universe since AHA-6796: a manual ['ng'] on an LP-derived
  // project survives the context reset.
  const universeFuels = useMemo(
    () => availableDuctedFuels(universeCodes),
    [universeCodes]
  );

  // Content key for the chassis-seed inputs (AHA-6796): cabinetChassisSize
  // is a SPEC field NOT encoded in componentsKey (uniqueId:replacing only),
  // so a spec-only chassis fill (analyzer completion writing onto an
  // existing component) must refresh the seed while unrelated store churn
  // must not. Derived through the same helper as the seed itself so the
  // key and the value cannot drift.
  const chassisSeedKey = useMemo(
    () =>
      ductedSeedChassisSizes(
        eligibleSystems,
        wantCompositionPicksBySystem
      ).join(','),
    [eligibleSystems, wantCompositionPicksBySystem]
  );

  // Chassis seed for the same once-per-context reset (AHA-6796): keyed on
  // the chassis content key — NOT componentsKey, which omits spec fields
  // and would return the stale pre-fill array after a spec-only chassis
  // fill. Within an ALREADY-reset context a fill does not force a re-reset
  // (the reducer no-ops on a repeated resultsKey) — the same accepted
  // imprecision as seedFuels; the fresh seed applies at the next context.
  const seedChassisSizes = useMemo(
    () => ductedSeedChassisSizes(eligibleSystems, wantCompositionPicksBySystem),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chassisSeedKey]
  );

  // Orientation seed, same shape as the chassis seed: content-keyed on the
  // spec-derived value (orientation is a SPEC field componentsKey omits),
  // so a spec-only orientation fill refreshes the seed at the next context
  // while unrelated store churn does not.
  const orientationSeedKey = useMemo(
    () => ductedSeedOrientations(eligibleSystems).join(','),
    [eligibleSystems]
  );
  const seedOrientations = useMemo(
    () => ductedSeedOrientations(eligibleSystems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orientationSeedKey]
  );

  const allWantPackagedUnit = useMemo(
    () =>
      everyDuctedSystemWantsPackagedUnit(
        eligibleSystems,
        wantCompositionPicksBySystem
      ),
    [eligibleSystems, wantCompositionPicksBySystem]
  );

  // The EE fuel row's equipment hint, from the same match sources as the
  // seed (helper kept beside ductedSeedFuels).
  const propaneSeedDescription = useMemo(
    () =>
      ductedPropaneSeedDescription(
        eligibleSystems,
        recommendationSelectionsBySystem
      ),
    [eligibleSystems, recommendationSelectionsBySystem]
  );

  const resetApplied = resetResultsKey === resultsKey;

  useEffect(() => {
    if (!isDucted || !allWantPackagedUnit) return;
    dispatch(clearDuctedChassisFilter());
  }, [isDucted, allWantPackagedUnit, dispatch]);

  useEffect(() => {
    // Ducted-path gating (hard constraint): zero effect work outside the
    // ducted pathway; and the wants hydration must land first — resetting
    // against the unhydrated mirror would seed off incomplete inputs.
    if (!isDucted || eligibleSystems.length === 0) return;
    if (!recommendationSelectionsLoaded) return;
    if (resetApplied) return;
    dispatch(
      resetDuctedSystemTypeCodes({
        resultsKey,
        seedFuels,
        seedChassisSizes,
        seedOrientations,
        universeFuels
      })
    );
    // eligibleSystems is derived from the resultsKey inputs — keying on the
    // string avoids refires from reference churn with unchanged content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDucted,
    resetApplied,
    resultsKey,
    seedFuels,
    seedChassisSizes,
    seedOrientations,
    universeFuels,
    recommendationSelectionsLoaded,
    dispatch
  ]);

  return {
    resetApplied,
    boundUnionCodes,
    universeCodes,
    seedChassisSizes,
    seedOrientations,
    propaneSeedDescription
  };
}
